import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CloseIcon, EditIcon } from "../../../icons";
import { DocumentEditor } from "./DocumentEditor";
import { useRootStore } from "../../../store/contexts";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export type InputHtmlStringProps = {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  fragment?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  autoFocus?: boolean;
  description?: string;
};

export const InputHtmlString: React.FC<InputHtmlStringProps> = observer(
  ({
    label,
    value,
    onChange,
    error = false,
    helperText = "",
    readOnly = false,
    fragment = false,
    disabled = false,
    hidden = false,
    description,
  }) => {
    const theme = useTheme();
    if (hidden) return null;
    const rootStore = useRootStore();
    const { langStore } = rootStore;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editorContent, setEditorContent] = useState("");

    const extractBodyContent = (html: string): string => {
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      return bodyMatch ? bodyMatch[1].trim() : html;
    };

    const handleOpenDialog = () => {
      setEditorContent(value);
      setDialogOpen(true);
    };

    const handleCloseDialog = () => {
      setDialogOpen(false);
      setEditorContent("");
    };

    const handleSave = () => {
      const finalContent = fragment
        ? extractBodyContent(editorContent)
        : editorContent;
      onChange?.(finalContent);
      handleCloseDialog();
    };

    const handleTemplateChange = (newContent: string) => {
      setEditorContent(newContent);
    };

    return (
      <Box sx={{ width: "100%", m: 1 }}>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          component="div"
        >
          <FieldLabelWithHelp label={label} description={description} />
        </Typography>

        <Box
          sx={{
            border: error
              ? "1px solid " + theme.palette.error.main
              : "1px solid " + theme.palette.divider,
            borderRadius: 1,
            p: 1,
            minHeight: 100,
            maxHeight: 200,
            overflow: "auto",
            backgroundColor: readOnly
              ? theme.palette.action.hover
              : theme.palette.background.paper,
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: value || `<p>${langStore.htmlEditorNoContent}</p>`,
            }}
          />
        </Box>

        {helperText && (
          <Typography
            variant="caption"
            color={error ? "error" : "textSecondary"}
            sx={{ ml: 1 }}
          >
            {helperText}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={handleOpenDialog}
            disabled={disabled || readOnly}
          >
            {langStore.htmlEditorEditButton}
          </Button>
        </Stack>

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">
                {readOnly ? langStore.viewCommand : langStore.editCommand}{" "}
                {label}
              </Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            <DocumentEditor
              renderedContent={editorContent}
              templateContent={editorContent}
              opened={true}
              initialMode={readOnly ? "readonly" : "edit"}
              onTemplateChange={handleTemplateChange}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {langStore.cancelCommand}
            </Button>
            {!readOnly && (
              <Button onClick={handleSave} variant="contained" color="primary">
                {langStore.htmlEditorSaveButton}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);
